
import { DashboardData, defaultDashboardData } from '@/types/dashboard';
import { fetchAllDashboardData } from './dashboardFetch';
import { calculateTrend } from './metricCalculations';
import { processNewsData } from './newsDataProcessor';
import { processLeaksData, countRecentLeaks } from './leakDataProcessor';
import { processCVEData } from './vulnerabilityProcessor';
import { processRansomwareData } from './ransomwareProcessor';
import { processEOLData, countUpcomingEOLItems } from './eolDataProcessors';
import { findMostAttackedCountry } from './countryDataProcessors';
import { getNewsFileName } from './newsFileSelector';

/**
 * Fetch and process all dashboard data
 */
export async function fetchDashboardData(language: string = 'en', days: number = 30): Promise<DashboardData> {
  try {
    const newsFileName = getNewsFileName(language);
    console.log(`fetchDashboardData using news file: ${newsFileName} for language: ${language}, days: ${days}`);

    const [_, leaksData, ransomwareData, cveData, __, newsData, eolData, releasesData] =
      await fetchAllDashboardData(newsFileName);

    const recentLeaks = processLeaksData(leaksData);
    const leaksCounts = countRecentLeaks(leaksData, days);
    const { recentRansomwareAttacks, allRansomwareAttacks, mostActiveGroup, victimCount, previousVictimCount } = processRansomwareData(ransomwareData, days);
    const {
      criticalVulnerabilities,
      highVulnerabilities,
      mediumVulnerabilities,
      lowVulnerabilities,
      recentCriticalVulnerabilities,
      prevCriticalCount,
      prevHighCount,
      prevMediumCount,
      prevLowCount
    } = processCVEData(cveData, days);

    console.log("News data received for language:", {
      language,
      newsFileName,
      hasNewsItems: newsData && (
        (Array.isArray(newsData) && newsData.length > 0) ||
        (newsData.items && newsData.items.length > 0) ||
        (newsData.news && newsData.news.length > 0)
      )
    });

    const latestNews = processNewsData(newsData);
    const upcomingEOL = processEOLData(eolData || {});
    const eolItemsCounts = countUpcomingEOLItems(eolData || {}, days);
    const mostAttackedCountry = findMostAttackedCountry(ransomwareData || []);

    const latestReleases = Array.isArray(releasesData)
      ? releasesData
        .sort((a, b) => new Date(b.data_wydania).getTime() - new Date(a.data_wydania).getTime())
        .slice(0, 5)
      : [];

    return {
      eolItemsCount: calculateTrend('eolItemsCount', eolItemsCounts.current, eolItemsCounts.previous),
      leaksCount: calculateTrend('leaksCount', leaksCounts.current, leaksCounts.previous),
      mostActiveRansomwareGroup: mostActiveGroup,
      mostAttackedCountry,
      ransomwareVictimsCount: calculateTrend('ransomwareVictimsCount', victimCount, previousVictimCount),
      criticalVulnerabilitiesCount: calculateTrend('criticalVulnerabilities', criticalVulnerabilities.length, prevCriticalCount),
      highVulnerabilitiesCount: calculateTrend('highVulnerabilities', highVulnerabilities.length, prevHighCount),
      mediumVulnerabilitiesCount: calculateTrend('mediumVulnerabilities', mediumVulnerabilities.length, prevMediumCount),
      lowVulnerabilitiesCount: calculateTrend('lowVulnerabilities', lowVulnerabilities.length, prevLowCount),
      latestNews,
      recentLeaks,
      recentCriticalVulnerabilities,
      upcomingEOL,
      latestReleases,
      recentRansomwareAttacks: recentRansomwareAttacks.slice(0, 5), // Ensure limit is 5
      allRansomwareAttacks
    };
  } catch (error) {
    console.error('Error in fetchDashboardData:', error);
    return defaultDashboardData;
  }
}
